import React, { useState, useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';
import Button from '../Button/Button';
import IconPlay from '../../files/play.svg';
import IconClose from '../../files/close.svg';
import IconChevron from '../../files/chevron.svg';

export const getEmbedUrl = (videoUrl) => {
  // regExpYoutube Handles:
  // Address bar on YouTube url (ex: http://www.youtube.com/watch?v=ZFqlHhCNBOI)
  // Direct http://youtu.be/ url (ex: http://youtu.be/ZFqlHhCNBOI)
  // Full iframe embed code (ex: <iframe src="http://www.youtube.com/embed/ZFqlHhCNBOI">)
  // Old <object> tag embed code (ex: <object><param name="movie" value="http://www.youtube.com/v/ZFqlHhCNBOI">...)
  const regExpYoutube = /(youtu\.be\/|youtube\.com\/(watch\?(.*&)?v=|(embed|v)\/))([^?&"'>]+)/;
  const matchYoutube = videoUrl.match(regExpYoutube);
  if (matchYoutube && matchYoutube[5]) {
    return 'https://www.youtube.com/embed/' + matchYoutube[5];
  }
  return new Error(`Could not generate an embed URL for ${videoUrl}`);
};

const VideoPlayer = ({
  source = 'youtube',
  videoUrl = '',
  posterImageUrl = '',
  popup = false,
  embedMode = 'responsive',
  iframeParams = 'autoplay=0',
}) => {
  const viewOnYoutubeBtnRef = useRef(null);
  const posterBtnRef = useRef(null);

  //set default image if popup enabled
  if (popup && posterImageUrl === '') {
    posterImageUrl =
      'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmci' +
      'IHZpZXdCb3g9IjAgMCA1MDAgNTAwIj48c3dpdGNoPjxnPjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZC' +
      'IgY2xpcC1ydWxlPSJldmVub2RkIiBmaWxsPSIjMDA0MjYyIiBkPSJNMCAwaDUwMHY1MDBIMHoiLz48' +
      'L2c+PC9zd2l0Y2g+PC9zdmc+';
  }

  const [showVideoPlayer, setShowVideoPlayer] = useState(posterImageUrl === '');

  useLayoutEffect(() => {
    if (showVideoPlayer && popup) {
      viewOnYoutubeBtnRef.current.focus();
    }
  }, [showVideoPlayer]);

  function handlePopupOpen() {
    //if popup already open, close it
    if (showVideoPlayer) return handlePopupClose();

    setShowVideoPlayer(true);
    document.body.classList.add('modal-open');
  }

  function handlePopupClose() {
    document.body.classList.remove('modal-open');
    setShowVideoPlayer(false);
    posterBtnRef.current.focus();
  }

  const handleVideoPlayerKeyDown = (evt) => {
    if (evt.key === 'Escape') {
      handlePopupClose();
    }
  };

  const focusLoopBtn = (
    <button
      style={{ width: 0, height: 0, opacity: 0 }}
      onFocus={() => viewOnYoutubeBtnRef.current.focus()}
    >
      Refocus
    </button>
  );

  const ytIframe = (
    <>
      <iframe
        title="YouTube video player"
        width="720"
        height="480"
        src={getEmbedUrl(videoUrl) + `?${iframeParams}`}
        frameBorder="0"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
      {popup && showVideoPlayer && focusLoopBtn}
    </>
  );

  return (
    <div
      onKeyDown={handleVideoPlayerKeyDown}
      className={classNames('video-player', {
        'video-player--popup': popup,
        'video-player--playing': showVideoPlayer,
        'video-player--fill': embedMode === 'fill',
        'video-player--responsive': embedMode === 'responsive',
        'video-player--has-poster': posterImageUrl !== '',
      })}
    >
      {
        /**
         * Display poster image if:
         * - poster image is available AND video is not playing OR;
         * - video is set to open in a popup
         */
        ((posterImageUrl && !showVideoPlayer) || popup) && (
          <button
            onClick={() =>
              popup ? handlePopupOpen() : setShowVideoPlayer(true)
            }
            className="video-player__poster-btn"
            style={{ backgroundImage: `url(${posterImageUrl})` }}
            aria-label="Play video"
            ref={posterBtnRef}
          >
            <IconPlay focusable={false} className="video-player__play-icon" />
            <span className="sr-only">Play video</span>
          </button>
        )
      }
      <div className="video-player__iframe-wrapper">
        {popup && (
          <div className="video-player__popup-controls">
            {focusLoopBtn}
            <a
              ref={viewOnYoutubeBtnRef}
              text="View on YouTube"
              href={videoUrl}
              rel="noreferrer"
              target="_blank"
              className="btn btn--filled video-player__youtube-btn"
              aria-label="View this video on YouTube's website"
              onClick={() => handlePopupClose()}
            >
              <span>View on Youtube</span>
              <IconChevron focusable={false} />
            </a>
            <Button
              className="video-player__close-btn"
              onClick={() => handlePopupClose()}
              aria-label="Close video"
            >
              <IconClose focusable={false} />
            </Button>
          </div>
        )}
        {source === 'youtube' && showVideoPlayer && ytIframe}
      </div>
      <div className="video-player__print-url" aria-hidden={true}>
        Video: {videoUrl}
      </div>
    </div>
  );
};

export default VideoPlayer;
